import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";

interface Track {
  id: string;
  src: string;
  title: string;
}

interface AudioPlayerContextType {
  currentTrack: Track | null;
  isPlaying: boolean;
  duration: number; // <-- NEW: Total duration of the track
  currentTime: number; // <-- NEW: Current playback time
  play: (track: Track) => void;
  pause: () => void;
  resume: () => void;
  seek: (time: number) => void; // <-- NEW: Function to change playback position
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(
  undefined
);

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Define callbacks with useCallback to stabilize them
  const onAudioEnded = useCallback(() => setIsPlaying(false), []);
  const onPlay = useCallback(() => setIsPlaying(true), []);
  const onPause = useCallback(() => setIsPlaying(false), []);
  const onTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);
  const onLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  }, []);

  // Effect to set up and tear down the audio element and its listeners
  useEffect(() => {
    if (typeof window !== "undefined" && !audioRef.current) {
      audioRef.current = new Audio();
    }
    const audioElement = audioRef.current;

    if (audioElement) {
      audioElement.addEventListener("ended", onAudioEnded);
      audioElement.addEventListener("play", onPlay);
      audioElement.addEventListener("pause", onPause);
      audioElement.addEventListener("timeupdate", onTimeUpdate);
      audioElement.addEventListener("loadedmetadata", onLoadedMetadata);
    }

    return () => {
      if (audioElement) {
        audioElement.removeEventListener("ended", onAudioEnded);
        audioElement.removeEventListener("play", onPlay);
        audioElement.removeEventListener("pause", onPause);
        audioElement.removeEventListener("timeupdate", onTimeUpdate);
        audioElement.removeEventListener("loadedmetadata", onLoadedMetadata);
      }
    };
  }, [onAudioEnded, onPlay, onPause, onTimeUpdate, onLoadedMetadata]);

  const play = useCallback((track: Track) => {
    if (audioRef.current) {
      setCurrentTrack(track);
      audioRef.current.src = track.src;
      audioRef.current.play();
    }
  }, []);

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
  }, []);

  const resume = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.play();
    }
  }, []);

  const seek = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const value = {
    currentTrack,
    isPlaying,
    duration,
    currentTime,
    play,
    pause,
    resume,
    seek,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
};

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (context === undefined) {
    throw new Error("useAudioPlayer must be used within an AudioProvider");
  }
  return context;
};
